import { push } from 'react-router-redux';

import service from '../utility/service';
import { TYPE, getRecordId } from '../utility/types';
import { getDefaultTimezone } from '../utility/date';
import RecordList, { updatedRecord, deletedRecord } from '../recordList/reducer';
import IsinDetail, { fetchIsinIfChanged, newRecord as isinDetailNewRecord, receivedRecord as isinDetailReceivedRecord } from '../isinDetail/reducer';
import { storeRecentlyUsedRecord } from '../recentlyUsed/reducer';
import { errorOccurred } from '../errors/reducer';

const INITIAL_STATE = {
  currentId: undefined,
  currentRecord: undefined,
  currentType: TYPE.ORDER,
  fetching: false,
  editing: false,
  unsavedChanges: false,
  saving: false,
  clipboardDate: null
};

const types = {
  FETCHING_RECORD: 'orb/recordDetail/FETCHING_RECORD',
  RECEIVED_RECORD: 'orb/recordDetail/RECEIVED_RECORD',
  CLEAR_CURRENT_RECORD: 'orb/recordDetail/CLEAR_CURRENT_RECORD',
  START_EDITING: 'orb/recordDetail/START_EDITING',
  SET_RECORD_PROPERTY: 'orb/recordDetail/SET_RECORD_PROPERTY',
  SAVING_RECORD: 'orb/recordDetail/SAVING_RECORD',
  SAVED_RECORD: 'orb/recordDetail/SAVED_RECORD',
  CANCELLED_EDITING: 'orb/recordDetail/CANCELLED_EDITING',
  NEW_RECORD: 'orb/recordDetail/NEW_RECORD',
  CREATED_RECORD: 'orb/recordDetail/CREATED_RECORD',
  FETCHING_CURRENT_TIME: 'orb/recordDetail/FETCHING_CURRENT_TIME',
  RECEIVED_CURRENT_TIME: 'orb/recordDetail/RECEIVED_CURRENT_TIME',
  REQUEST_FAILED: 'orb/recordDetail/REQUEST_FAILED',
  COPY_DATE: 'orb/recordDetail/COPY_DATE',
  BEGIN_EXECUTE_ORDER: 'orb/recordDetail/BEGIN_EXECUTE_ORDER',
  FETCHING_NOTIONAL: 'orb/recordDetail/FETCHING_NOTIONAL',
  RECEIVED_NOTIONAL: 'orb/recordDetail/RECEIVED_NOTIONAL',
};

const apiPaths = {
  rfq: 'RFQs',
  order: 'Orders'
};
const newRecordPaths = {
  rfq: '/rfq',
  order: '/order'
};


const templateNewRecords = {
  rfq: {},
  order: {
    ExecutionStatus: 'Working'
  }
};

export default function reducer(state = INITIAL_STATE, action = {}) {
  const clearedRecord = {
    currentId: undefined,
    currentRecord: undefined,
    editing: false,
    unsavedChanges: false
  };

  switch (action.type) {
    case types.FETCHING_RECORD:
      return Object.assign({}, state, {
        fetching: true,
        editing: false,
        unsavedChanges: false
      });
    case RecordList.types.CLEAR_RECORDS:
      // Clear current record if list is cleared
      if (action.keepCurrentRecord) {
        return state;
      }
      return Object.assign({}, state, clearedRecord);
    case types.CLEAR_CURRENT_RECORD:
      return Object.assign({}, state, clearedRecord);
    case types.START_EDITING:
      return Object.assign({}, state, {
        editing: true
      });
    case types.RECEIVED_RECORD:
      return Object.assign({}, state, {
        fetching: false,
        currentType: action.recordType,
        currentId: action.recordId,
        currentRecord: action.record
      });
    case types.SET_RECORD_PROPERTY:
    {
      const record = Object.assign({}, state.currentRecord);
      record[action.propertyName] = action.value;
      return Object.assign({}, state, {
        currentRecord: record,
        unsavedChanges: true
      });
    }
    case IsinDetail.types.RECEIVED_PRODUCT:
      return Object.assign({}, state, {
        currentRecord: Object.assign({}, state.currentRecord, {
          Product: productFromLookup(state.currentRecord.Product, action.product)
        }),
        unsavedChanges: true
      });
    case IsinDetail.types.DEFAULT_RECORD_PROPERTY:
      if (action.propertyName === 'FrontOfficeSystem'
                  && !state.currentRecord.TicketReferences) {
        const defaults = {};
        defaults[action.propertyName] = action.value;

        return Object.assign({}, state, {
          currentRecord: Object.assign({}, state.currentRecord, defaults),
          unsavedChanges: true
        });
      }
      return state;
    case types.SAVING_RECORD:
      return Object.assign({}, state, {
        saving: true
      });
    case types.SAVED_RECORD:
      return Object.assign({}, state, {
        editing: false,
        unsavedChanges: false,
        saving: false,
        currentRecord: action.record
      });
    case types.CANCELLED_EDITING:
      // Clear current record for reloading
      return Object.assign({}, state, {
        currentRecord: undefined,
        editing: false,
        unsavedChanges: false
      });
    case types.NEW_RECORD:
      return Object.assign({}, state, {
        editing: true,
        currentId: undefined,
        currentType: action.recordType,
        currentRecord: action.record
      });
    case types.CREATED_RECORD:
      return Object.assign({}, state, {
        editing: false,
        unsavedChanges: false,
        saving: false,
        currentId: action.id,
        currentRecord: action.record
      });
    case types.REQUEST_FAILED:
      return Object.assign({}, state, {
        fetching: false,
        saving: false,
        currentId: action.recordId || state.currentId,
        currentType: action.recordType || state.currentType,
      });
    case types.COPY_DATE:
      return Object.assign({}, state, {
        clipboardDate: action.value
      });
    case types.BEGIN_EXECUTE_ORDER:
    {
      const executedRecord = Object.assign({}, state.currentRecord);
      executedRecord.ExecutionDate = action.execDate;
      return Object.assign({}, state, {
        currentRecord: executedRecord
      });
    }
    case types.RECEIVED_NOTIONAL:
    {
      const currentRecord = Object.assign({}, state.currentRecord);
      currentRecord.Notional = action.Notional;
      return Object.assign({}, state, { currentRecord });
    }
    default:
      return state;
  }
}
reducer.types = types;
reducer.initialState = INITIAL_STATE;

function productFromLookup(currentProduct, product) {
  const { CFICode, Isin, ProductId } = product;
  return {
    CFICode,
    ProductId,
    Isin: Isin || (currentProduct && currentProduct.Isin)
  };
}

function fetchingRecord(recordType) {
  return {
    type: types.FETCHING_RECORD,
    recordType
  };
}

function receivedRecord(recordType, recordId, record) {
  return {
    type: types.RECEIVED_RECORD,
    recordType,
    recordId,
    record
  };
}

function shouldFetchRecord(state, type, id) {
  if (state.fetching ||
    (state.currentId === id && state.currentType === type)) {
    return false;
  }
  return true;
}

export function startEditing() {
  return {
    type: types.START_EDITING
  };
}

export function cancelledEditing() {
  return {
    type: types.CANCELLED_EDITING
  };
}

function savingRecord() {
  return {
    type: types.SAVING_RECORD
  };
}

function savedRecord(record) {
  return {
    type: types.SAVED_RECORD,
    record
  };
}

function createdRecord(id, record) {
  return {
    type: types.CREATED_RECORD,
    id,
    record
  };
}

function requestFailed(type, id) {
  return {
    type: types.REQUEST_FAILED,
    recordType: type,
    recordId: id
  };
}

export function fetchRecordIfNeeded(type, id) {
  return (dispatch, getState) => {
    if (shouldFetchRecord(getState().recordDetail, type, id)) {
      return fetchRecord(dispatch, type, id);
    }
    return Promise.resolve();
  };
}

function fetchRecord(dispatch, type, id) {
  dispatch(fetchingRecord(type));

  return service.get(`/${apiPaths[type]}/${id}`).then((response) => {
    dispatch(receivedRecord(type, id, response.data));
    dispatch(isinDetailReceivedRecord());
  }).catch((err) => {
    dispatch(errorOccurred(err, `Getting ${type} id ${id}`));
    dispatch(requestFailed(type, id));
  });
}

function putEditedRecord(type, id, record) {
  return dispatch => service.put(`/${apiPaths[type]}/${id}`, record).then((response) => {
    const returnedRecord = response.data;
    dispatch(savedRecord(returnedRecord));
    // If the record was deleted, then refresh the Record List
    if (recordIsDeleted(returnedRecord)) {
      dispatch(deleteCurrentRecord(type, id));
    } else {
      // Update the record in the list
      dispatch(updatedRecord(type, id));
    }
  }).catch((err) => {
    dispatch(errorOccurred(err, `Saving ${type} id ${id}`));
    dispatch(requestFailed());
  });
}

function recordIsDeleted(record) {
  if (record.Status === 'Deleted' || record.ExecutionStatus === 'Deleted') {
    return true;
  }
  return false;
}

function deleteCurrentRecord(type, id) {
  return (dispatch) => {
    const url = `${newRecordPaths[type]}`;
    dispatch(deletedRecord(type, id));
    dispatch(clearCurrentRecord());
    dispatch(push(url));
  };
}

function clearCurrentRecord() {
  return {
    type: types.CLEAR_CURRENT_RECORD
  };
}

function postNewRecord(type, record) {
  return (dispatch, getState) => {
    const searchParams = getState().recordList.filterQuerystring;

    return service.post(`/${apiPaths[type]}`, record).then((response) => {
      const returnedRecord = response.data;
      const id = getRecordId(type, returnedRecord);

      dispatch(createdRecord(id, returnedRecord));
      // Update the record in the list
      dispatch(updatedRecord(type, id));

      // Navigate to view it
      const url = `${newRecordPaths[type]}/${id}${searchParams}`;
      dispatch(push(url));
    }).catch((err) => {
      dispatch(errorOccurred(err, `Saving new ${type}`));
      dispatch(requestFailed());
    });
  };
}

function checkIsinUpdated(dispatch, getState) {
  const fetchAction = fetchIsinIfChanged(getState().isinDetail.product.InstrumentTypeId);

  return fetchAction(dispatch, getState);
}

export function saveRecord(updatedProperties) {
  return (dispatch, getState) =>
    // make sure the Isin check gets done first
    checkIsinUpdated(dispatch, getState).then((wasSaved) => {
      if (!wasSaved) {
        return Promise.resolve();
      }
      // Now save the record
      const state = getState().recordDetail;
      dispatch(savingRecord());

      const type = state.currentType;
      const record = Object.assign({}, state.currentRecord, updatedProperties);
      dispatch(storeRecentlyUsedRecord(record));
      const id = state.currentId;

      if (!id) {
        return dispatch(postNewRecord(type, record));
      }
      return dispatch(putEditedRecord(type, id, record));
    });
}

export function cancelEditing() {
  return (dispatch, getState) => {
    const state = getState().recordDetail;
    const type = state.currentType;
    const id = state.currentId;

    dispatch(cancelledEditing());

    if (id) {
      return fetchRecord(dispatch, type, id);
    }
    return Promise.resolve();
  };
}

export function setRecordProperty(propertyName, value) {
  return {
    type: types.SET_RECORD_PROPERTY,
    propertyName,
    value
  };
}

function fetchingCurrentTime() {
  return {
    type: types.FETCHING_CURRENT_TIME
  };
}

function receivedCurrentTime() {
  return {
    type: types.RECEIVED_CURRENT_TIME
  };
}

export function setCurrentTime(timePropertyName, timeZoneName) {
  return dispatch => dispatch(getCurrentTime(timeZoneName))
    .then((currentTime) => {
      if (currentTime) {
        dispatch(setRecordProperty(timePropertyName, currentTime));
      }
    });
}

export function getCurrentTime(timeZoneName) {
  return (dispatch) => {
    dispatch(fetchingCurrentTime());
    const zone = timeZoneName || getDefaultTimezone();

    return service.get(`/DateTime?timezone=${zone}`).then((response) => {
      dispatch(receivedCurrentTime());
      return response.data;
    }).catch((err) => {
      dispatch(errorOccurred(err, `Getting current time in ${zone}`));
    });
  };
}

function editNewRecord(recordType, record) {
  return {
    type: types.NEW_RECORD,
    recordType,
    record
  };
}

function createNewRecord(type, state) {
  const record = Object.assign({}, templateNewRecords[type]);
  record.SalesPerson = state.user && state.user.current;

  const businessGroups = state.staticData.businessGroups;
  if (businessGroups && businessGroups.length === 1) {
    record.BusinessGroup = businessGroups[0];
  }

  return record;
}

function cloneRecord(type, state, record) {
  const cloned = Object.assign({}, record);
  delete cloned.OrderNumber;
  delete cloned.RFQNumber;
  cloned.SalesPerson = state.user && state.user.current;

  return cloned;
}

export function newRecord(clonedFrom) {
  return (dispatch, getState) => {
    const state = getState();
    const type = state.recordList.type;
    const searchParams = state.recordList.filterQuerystring;

    dispatch(isinDetailNewRecord());

    // Start editing based on an empty or cloned record
    const record = clonedFrom
      ? cloneRecord(type, state, clonedFrom)
      : createNewRecord(type, state);

    dispatch(editNewRecord(type, record));

    // Navigate to new-record url
    const url = `${newRecordPaths[type]}${searchParams}`;

    dispatch(push(url));

    return Promise.resolve();
  };
}

export function copyDate(value) {
  return {
    type: types.COPY_DATE,
    value,
  };
}

export function beginExecuteOrder(execDate) {
  return {
    type: types.BEGIN_EXECUTE_ORDER,
    execDate,
  };
}

export function setAcceptedTime(timeZoneName) {
  return (dispatch, getState) => {
    const acceptedAtProperty = 'AcceptedAt';
    // Set AcceptedAt timestamp
    dispatch(setCurrentTime(acceptedAtProperty, timeZoneName)).then(() => {
      // Use the same acceptance and ExecutedAt timestamp
      const state = getState();
      const currentRecord = state.recordDetail.currentRecord;
      if (currentRecord && currentRecord[acceptedAtProperty]) {
        dispatch(setRecordProperty('ExecutedAt', currentRecord[acceptedAtProperty]));
      }
    });
  };
}

export function fetchNotional(oldVal, newVal) {
  return (dispatch, getState) => {
    if (oldVal === newVal) {
      return null;
    }
    const state = getState();
    const currentRecord = state.recordDetail.currentRecord;

    // Get client rate and quantity from order or price and size from RFQ (Lazy hack)
    const { UnitType, ClientRate, Price, Quantity, Size } = currentRecord;
    let priceVal = 0;
    let qtyVal = 0;
    if (ClientRate || Price) {
      priceVal = !ClientRate ? Price : ClientRate;
    } else {
      priceVal = 0;
    }


    if (Quantity || Size) {
      qtyVal = !Quantity ? Size : Quantity;
    }
    if (!qtyVal) {
      return null;
    }

    dispatch(fetchingNotional());
    const unit = !UnitType ? '' : UnitType;
    return service.get(`/Notional?price=${priceVal}&qty=${qtyVal}&unit=${unit}`).then((response) => {
      dispatch(receivedNotional(response.data.Notional));
    }).catch((err) => {
      dispatch(errorOccurred(err, 'Getting notional'));
    });
  };
}

function fetchingNotional() {
  return {
    type: types.FETCHING_NOTIONAL
  };
}

function receivedNotional(notional) {
  return {
    type: types.RECEIVED_NOTIONAL,
    Notional: notional
  };
}



// WEBPACK FOOTER //
// ./src/recordDetail/reducer.js